import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";

import { baseConfig } from "./../../../axiosConfig";
import { Context } from "./../../../contexts/Store";
import tickIconGreen from "../../../assets/icons/tick.svg";
import fileUploadIcon from "../../../assets/icons/file_upload.svg";
import closeIcon from "../../../assets/icons/close.png";
import reUploadIcon from "../../../assets/icons/re-upload-icon.svg";
import chevronUp from "../../../assets/icons/chevron-up.svg";
import chevronDown from "../../../assets/icons/chevron-down.svg";

import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../includes/loader/Loader";
import {
  formatFileSize,
  normalizeFileType,
  getAcceptedFileTypes,
  timeFormatter,
  getFileIcon,
  getExtensionTextColor
} from "./fileUtils";

// ============= UTILITY FUNCTIONS =============


const getFileTypeColor = (selectedFile, itemFileType) => {
  const normalizedSelected = normalizeFileType(selectedFile);
  const normalizedItem = normalizeFileType(itemFileType);

  if (normalizedSelected === "pdf" && normalizedItem === "pdf")
    return "#FFEBEE";

  if (normalizedSelected === "ppt" && normalizedItem === "ppt")
    return "#FFF3E0";

  if (normalizedSelected === "word" && normalizedItem === "word")
    return "#E3F2FD";

  if (normalizedSelected === "xlsx" && normalizedItem === "xlsx")
    return "#E7F5ED";

  if (
    ["jpg", "png", "gif", "webp", "svg", "image"].includes(
      normalizedSelected
    ) &&
    ["jpg", "png", "gif", "webp", "svg", "image"].includes(normalizedItem)
  )
    return "#E3F2FD";

  if (
    ["mp4", "video"].includes(normalizedSelected) &&
    ["mp4", "video"].includes(normalizedItem)
  )
    return "#F3E5F5";

  return "#ffffff";
};

// Process submitted files by file type index from file_requirements
const processFileTypeSubmissions = (
  submittedFiles,
  fileRequirements,
  globalStatus
) => {
  // Create an object with keys as file type indices
  const submissionsByType = {};

  // Initialize arrays for each file type
  fileRequirements.forEach((_, index) => {
    submissionsByType[index] = [];
  });

  let latestGlobalIndex = -1;

  // Pass 1: Group submissions and find various indices
  submittedFiles.forEach((submission) => {
    // Find which file type this belongs to based on the chapter mapping
    let typeIndex = -1;
    if (submission.chapter) {
      const chapterNum = Number(submission.chapter);
      // DYNAMIC: Chapter number is index + 1, so reverse it
      const chapterIndex = chapterNum - 1;

      // Verify the index is valid
      if (chapterIndex >= 0 && chapterIndex < fileRequirements.length) {
        typeIndex = chapterIndex;
      }
    } else {
      // Only fallback to file_type_index if chapter is NOT present
      if (submission.file_type_index !== undefined) {
        typeIndex = submission.file_type_index;
      }
    }

    // Capture the index of the submission marked as 'is_latest' globally
    if (submission.is_latest) {
      latestGlobalIndex = typeIndex;
    }

    // Only process if typeIndex is valid found in fileRequirements.
    if (typeIndex !== -1 && submissionsByType[typeIndex] !== undefined) {
      submissionsByType[typeIndex].push({
        ...submission, // Keep original data for sorting
        files: submission.files.map((file) => ({
          fileName: file.file_name,
          fileUrl: file.file_url,
          fileType: file.feedback_file_type || file.file_name?.split(".").pop(),
          uploadedDate: file.submitted_date,
        })),
        submittedDate: submission.submitted_date,
        feedbackDate: submission.feedback_date,
        feedback_file_name: submission.feedback_file_name,
        feedback_file_type: submission.feedback_feedback_type,
        // Status will be assigned in Pass 2
      });
    }
  });

  // Pass 2: Assign status based on chapter logic
  Object.keys(submissionsByType).forEach((key) => {
    const typeIndex = parseInt(key);
    const submissions = submissionsByType[typeIndex];

    if (submissions.length === 0) return;

    // Find max version for this chapter
    const maxVersion = Math.max(...submissions.map((s) => s.version || 0));

    submissions.forEach((submission) => {
      let status = "Returned";

      if (typeIndex < latestGlobalIndex) {
        // Previous chapters: Highest version is Approved, others Returned
        if (submission.version === maxVersion) {
          status = "Approved";
        }
      } else if (typeIndex === latestGlobalIndex) {
        // Current chapter: Latest flag determines Global Status
        if (submission.is_latest) {
          status = globalStatus;
        }
      }
      // Future chapters (if any exist > latestGlobalIndex) default to Returned

      // Assign the calculated status to the processed object
      submission.status = status;
      submission.typeIndex = typeIndex; // Ensure typeIndex is set on the object
      submission.isLatest = submission.is_latest || false; // Ensure isLatest is set consistently
      submission.feedback = submission.feedback || "";
      submission.upload = submission.upload || null;
    });
  });

  return submissionsByType;
};

// Determine which file types are locked/unlocked based on sequential approval
const determineFileTypeStatuses = (fileRequirements, processedSubmissions) => {
  const statuses = {};

  // First file type is always unlocked initially
  fileRequirements.forEach((_, index) => {
    statuses[index] = "locked";
  });

  // Unlock first file type
  if (fileRequirements.length > 0) {
    statuses[0] = "active";
  }

  // Check each file type sequentially
  for (let i = 0; i < fileRequirements.length; i++) {
    const submissions = processedSubmissions[i] || [];

    // Use the submission with the highest version to determine status
    // For previous chapters, isLatest will be false for all files, so we must sort by version
    const latestVersionSubmission = [...submissions].sort(
      (a, b) => b.version - a.version
    )[0];

    if (latestVersionSubmission) {
      // Update current file type status
      statuses[i] = latestVersionSubmission.status;

      // If current is approved, unlock next
      if (
        latestVersionSubmission.status === "Approved" &&
        i + 1 < fileRequirements.length
      ) {
        // Only modify next status if it hasn't been set to something more specific derived from its own files
        const nextSubmissions = processedSubmissions[i + 1] || [];
        if (nextSubmissions.length > 0) {
          const nextLatest = [...nextSubmissions].sort(
            (a, b) => b.version - a.version
          )[0];
          statuses[i + 1] = nextLatest ? nextLatest.status : "active";
        } else {
          statuses[i + 1] = "active";
        }
      }
    }
  }

  return statuses;
};

const getLatestFileTypeStatus = (typeIndex, processedSubmissions) => {
  const submissions = processedSubmissions[typeIndex];
  if (!submissions || submissions.length === 0) return null;
  const latestSubmission = [...submissions].sort(
    (a, b) => b.version - a.version
  )[0];
  return latestSubmission ? latestSubmission.status : null;
};

// LoadedFileDiv component
const LoadedFileDiv = React.memo(
  ({
    file,
    setUploadedFiles,
    setFileArray,
    setSubmitButton,
    normalizeFileType,
    selectedFile,
    onDelete,
  }) => {
    const formattedSize = useMemo(
      () => formatFileSize(file.file?.size),
      [file.file?.size]
    );
    const extension = useMemo(
      () => file.file?.name.split(".").pop(),
      [file.file?.name]
    );

    const handleDelete = useCallback(() => {
      if (onDelete) {
        onDelete();
        return;
      }
      const fileExtension = file.file?.name.split(".").pop();
      const deletedFileType = normalizeFileType(fileExtension);

      setUploadedFiles((prev) =>
        prev.filter((item) => item.file !== file.file)
      );

      setFileArray((prev) => {
        return prev.map((item) => {
          // Match by file_type if this matches the current selectedFile
          if (item.file_type === selectedFile) {
            return { ...item, count: item.count + 1 };
          }

          // Fallback to normalized matching for standard file types
          const itemType = normalizeFileType(item.file_type);
          if (itemType === deletedFileType) {
            return { ...item, count: item.count + 1 };
          }
          return item;
        });
      });
    }, [
      file.file,
      selectedFile,
      setUploadedFiles,
      setSubmitButton,
      setFileArray,
      normalizeFileType,
      onDelete,
    ]);

    return (
      <FileCard>
        <FileCardLink
          href={file.file ? URL.createObjectURL(file.file) : file.file_url}
          target="_blank"
        >
          <FileCardContent>
            <FileIcon src={getFileIcon(extension)} />
            <FileInfo>
              <FileName>{file.file?.name || file.file_url}</FileName>
              <FileMeta>
                {formattedSize && <FileSize>{formattedSize}</FileSize>}
                {file.date && <FileDate>{file.date}</FileDate>}
              </FileMeta>
            </FileInfo>
          </FileCardContent>
        </FileCardLink>
        {file.file?.size && (
          <DeleteButton>
            <FileIcon
              src={closeIcon}
              style={{ width: "13px", height: "13px", cursor: "pointer" }}
              onClick={handleDelete}
            />
          </DeleteButton>
        )}
      </FileCard>
    );
  }
);

// DYNAMIC: Get section name by chapter number from file_requirements
const getSectionNameByChapter = (chapter, fileRequirements) => {
  if (!fileRequirements || fileRequirements.length === 0) {
    return `Chapter ${chapter}`;
  }

  // Chapter number is index + 1, so find by index
  const chapterIndex = Number(chapter) - 1;

  if (chapterIndex >= 0 && chapterIndex < fileRequirements.length) {
    return fileRequirements[chapterIndex].file_type;
  }

  return `Chapter ${chapter}`;
};

const SubmitDissertation = ({
  setSinglePage,
  refreshApi,
  assignmentDetails,
  fileArray,
  selectedFile,
  setFileArray,
  setSelectedFile,
}) => {
  const { state } = useContext(Context);
  const { id } = useParams();
  const navigate = useNavigate();

  // LOCAL state for selectedFile (don't rely on parent prop)
  const [localSelectedFile, setLocalSelectedFile] = useState(
    selectedFile || fileArray?.[0]?.file_type || null
  );

  // Initialize localSelectedFile when fileArray changes
  useEffect(() => {
    if (!localSelectedFile && fileArray?.length > 0) {
      setLocalSelectedFile(fileArray[0].file_type);
    }
  }, [fileArray, localSelectedFile]);

  // State for initial files (normal submission)
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [similarityReport, setSimilarityReport] = useState({
    file: null,
    percentage: null,
    status: null,
  });

  // Dynamic state for ALL dissertation file types (based on file_requirements)
  const [dissertationFiles, setDissertationFiles] = useState({});
  // Example structure: { "Initial Proposal": [...files], "Final Proposal": [...files] }

  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // State for file type submissions (dynamic from file_requirements)
  const [processedSubmissions, setProcessedSubmissions] = useState({});
  const [fileTypeStatuses, setFileTypeStatuses] = useState({});
  const [expandedVersions, setExpandedVersions] = useState({});

  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  const globalStatus = assignmentDetails?.status;

  useEffect(() => {
    if (assignmentDetails?.file_requirements) {
      setFileArray(assignmentDetails.file_requirements);
    }
  }, [assignmentDetails]);

  // Process file type submissions from backend
  useEffect(() => {
    if (assignmentDetails?.submitted_files && fileArray?.length > 0) {
      const processed = processFileTypeSubmissions(
        assignmentDetails.submitted_files,
        fileArray,
        globalStatus
      );
      setProcessedSubmissions(processed);
    }
  }, [assignmentDetails, globalStatus, fileArray]);

  // Determine file type unlock statuses based on sequential approval
  useEffect(() => {
    if (fileArray?.length > 0) {
      const statuses = determineFileTypeStatuses(
        fileArray,
        processedSubmissions
      );
      setFileTypeStatuses(statuses);
    }
  }, [fileArray, processedSubmissions]);

  // Submit files from SELECTED section only (DYNAMIC)
  const submitInitialFiles = useCallback(() => {
    let accessToken = state.user_details.access_token;
    const formdata = new FormData();

    // Find the selected file type in file_requirements
    const selectedIndex = fileArray.findIndex(
      (item) => item.file_type === selectedFile
    );

    if (selectedIndex === -1) {
      console.error("Selected file type not found in file_requirements");
      return;
    }

    // Chapter number is array index + 1 (0 becomes 1, 1 becomes 2, etc.)
    const chapterNumber = selectedIndex + 1;

    // Get files for selected type
    const filesToSubmit = dissertationFiles[selectedFile] || [];

    // Only submit files from the selected section
    if (filesToSubmit && filesToSubmit.length > 0) {
      filesToSubmit.forEach((item) => {
        formdata.append("submitted_file", item.file);
        formdata.append("chapter", chapterNumber);
      });
    }

    setIsLoading(true);
    baseConfig
      .post(
        `/students/assignment-submit/${id || assignmentDetails.assignment_id}/`,
        formdata,
        {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        }
      )
      .then((response) => {
        const { StatusCode } = response.data;
        if (StatusCode === 6000) {
          // Clear only the submitted section's files (DYNAMIC)
          setIsLoading(false);
          setDissertationFiles((prev) => ({
            ...prev,
            [selectedFile]: [], // Clear only the selected file type
          }));
          setSinglePage && setSinglePage(false);
          refreshApi && refreshApi();
          if (!setSinglePage && !refreshApi) {
            navigate("/assignments/");
          }
        } else {
          alert("Contact LMS");
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (error?.response?.data?.code === "token_not_valid") {
          localStorage.clear();
          window.location = "/";
        }
        setIsLoading(false);
        console.error("Submit error:", error);
        alert("Contact LMS");
      });
  }, [
    selectedFile,
    dissertationFiles,
    fileArray,
    id,
    assignmentDetails,
    state.user_details.access_token,
    setSinglePage,
    refreshApi,
    navigate,
  ]);

  // Robust submit button logic - only check CURRENTLY SELECTED tab (DYNAMIC)
  useEffect(() => {
    if (!fileArray || fileArray.length === 0) return;

    // Find the currently selected file type in fileArray
    const selectedFileTypeItem = fileArray.find(
      (item) => item.file_type === selectedFile
    );

    if (!selectedFileTypeItem) {
      setSubmitButton(false);
      return;
    }

    // Get the status for the selected file type
    const typeIndex = fileArray.findIndex(
      (item) => item.file_type === selectedFile
    );
    const status = fileTypeStatuses[typeIndex];

    // If locked or already approved, don't allow submission
    if (status === "locked" || status === "Approved") {
      setSubmitButton(false);
      return;
    }

    // Check if currently selected tab has files ready (DYNAMIC)
    const filesForSelectedType = dissertationFiles[selectedFile] || [];
    const hasFiles = filesForSelectedType.length > 0;

    setSubmitButton(hasFiles);
  }, [fileArray, selectedFile, fileTypeStatuses, dissertationFiles]);

  const handleReport = useCallback(
    async (e) => {
      let accessToken = state.user_details.access_token;
      const formData = new FormData();
      formData.append("submitted_file", e.target.files[0]);

      try {
        const { data } = await baseConfig.post(
          "/students/similarity-check/",
          formData,
          {
            headers: {
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        setSimilarityReport({
          file: e.target.files[0],
          status: data.data.status,
          percentage: data.data.percentage,
        });

        if (data.data.status === "valid") {
          setFileArray((prev) =>
            prev.map((item) => {
              const itemType = normalizeFileType(item.file_type);

              if (itemType === "Similarity Report") {
                return { ...item, count: Math.max(0, item.count - 1) };
              }
              return item;
            })
          );

          const now = new Date();
          const formattedTime = now.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          });

          const newFileObjects = {
            file: e.target.files[0],
            date: formattedTime,
          };

          setUploadedFiles((prev) => [...prev, newFileObjects]);

          const position = fileArray.findIndex(
            (item) => normalizeFileType(item.file_type) === "Similarity Report"
          );
          if (position + 1 < fileArray.length) {
            setSelectedFile(fileArray[position + 1].file_type);
          }
        }
      } catch (error) {
        alert("Please upload the correct similarity report");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      state.user_details.access_token,
      fileArray,
      setFileArray,
      setSelectedFile,
      setUploadedFiles,
      selectedFile,
    ]
  );

  const handleFileUpload = useCallback(
    (e) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }

      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const now = new Date();
      const formattedTime = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      const newDissertationFiles = files.map((file) => ({
        file: file,
        date: formattedTime,
      }));

      // Check if this is a dissertation file type (exists in file_requirements)
      const isDissertationType = fileArray.some(
        (item) => item.file_type === selectedFile
      );

      if (isDissertationType) {
        // Add files to dissertationFiles dynamically
        setDissertationFiles((prev) => ({
          ...prev,
          [selectedFile]: [
            ...(prev[selectedFile] || []),
            ...newDissertationFiles,
          ],
        }));

        // Update file count
        setFileArray((prev) =>
          prev.map((item) =>
            item.file_type === selectedFile
              ? { ...item, count: Math.max(0, item.count - files.length) }
              : item
          )
        );

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // For custom file types like "Initial Proposal", "Final Proposal", "Viva"
      // match based on the currently selected file type
      let fileDetails = fileArray.find(
        (item) => item.file_type === selectedFile
      );

      // If not found, try normalized matching (for standard file types)
      if (!fileDetails) {
        fileDetails = fileArray.find((item) => {
          const normalizedItemType = normalizeFileType(item.file_type);
          const normalizedFileExt = normalizeFileType(fileExtension);

          return (
            normalizedItemType === normalizedFileExt ||
            item.file_type === fileExtension
          );
        });
      }

      if (!fileDetails) {
        alert("No matching file details found for this file type.");
        return;
      }

      const existingFileNames = new Set(
        uploadedFiles.map((item) => item.file.name)
      );
      const duplicates = files.filter((file) =>
        existingFileNames.has(file.name)
      );

      if (duplicates.length > 0) {
        const duplicateFileNames = duplicates
          .map((file) => ` - ${file.name}`)
          .join("\n");
        alert(
          `The following file(s) have already been uploaded:\n${duplicateFileNames}`
        );
        e.target.value = null;
        return;
      }

      const dedicatedCountArray = uploadedFiles.filter((item) => {
        const uploadedExt = item.file.name.split(".").pop();
        return (
          normalizeFileType(uploadedExt) === normalizeFileType(fileExtension)
        );
      });

      if (
        (fileDetails.count % 2 === 0
          ? fileDetails.count >= dedicatedCountArray.length
          : fileDetails.count + 1 >= dedicatedCountArray.length) &&
        files.length <= fileDetails.count
      ) {
        const now = new Date();
        const formattedTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        });

        const newFileObjects = files.map((file) => ({
          file: file,
          date: formattedTime,
        }));

        setUploadedFiles((prev) => [...prev, ...newFileObjects]);

        setFileArray((prev) =>
          prev.map((item) => {
            // Match by selectedFile for custom file types (like Initial Proposal)
            // or by normalized type for standard file types
            if (item.file_type === selectedFile) {
              return { ...item, count: Math.max(0, item.count - files.length) };
            }

            // Fallback to normalized matching for standard file types
            const itemType = normalizeFileType(item.file_type);
            const uploadedType = normalizeFileType(fileExtension);
            if (itemType === uploadedType) {
              return { ...item, count: Math.max(0, item.count - files.length) };
            }

            return item;
          })
        );

        if (files.length === fileDetails.count) {
          const position = fileArray.findIndex(
            (item) => item.file_type === fileDetails.file_type
          );
          if (position + 1 < fileArray.length) {
            setSelectedFile(fileArray[position + 1].file_type);
          }
        }
      } else {
        alert(`Count of ${fileExtension} files exceeds the allowed limit.`);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      uploadedFiles,
      fileArray,
      setUploadedFiles,
      setFileArray,
      setSelectedFile,
      selectedFile,
    ]
  );

  // Single dynamic delete handler for all dissertation file types
  const handleDeleteDissertationFile = useCallback(
    (fileType, index) => {
      setDissertationFiles((prev) => ({
        ...prev,
        [fileType]: (prev[fileType] || []).filter((_, i) => i !== index),
      }));
      setFileArray((prev) =>
        prev.map((item) =>
          item.file_type === fileType
            ? { ...item, count: item.count + 1 }
            : item
        )
      );
    },
    [setFileArray]
  );

  // Handle tab selection with status check
  const handleFileSelection = useCallback(
    (fileType) => {
      const fileTypeIndex = fileArray.findIndex(
        (item) => item.file_type === fileType
      );

      if (fileTypeIndex !== -1) {
        // Check if this file type is locked
        if (fileTypeStatuses[fileTypeIndex] === "locked") {
          alert("Please wait for previous file type to be approved.");
          return;
        }
      }
      setSelectedFile(fileType);
    },
    [fileArray, fileTypeStatuses, setSelectedFile]
  );

  const handleDragEnter = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
    dragCounter.current++;

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = Array.from(e.dataTransfer.files);

      const acceptedTypes = getAcceptedFileTypes(selectedFile, fileArray);
      const validFiles = files.filter((file) => {
        const extension = "." + file.name.split(".").pop().toLowerCase();
        return acceptedTypes === "*/*" || acceptedTypes.includes(extension);
      });

      if (validFiles.length === 0) {
        alert(`Please drop ${acceptedTypes} files only`);
        return;
      }

      if (selectedFile === "Similarity Report") {
        const event = { target: { files: [validFiles[0]] } };
        handleReport(event);
      } else {
        const event = { target: { files: validFiles } };
        handleFileUpload(event);
      }
    },
    [selectedFile, handleReport, handleFileUpload]
  );

  const isDropZoneActive = useMemo(() => {
    const currentFileType = fileArray?.find(
      (item) => item.file_type === selectedFile
    );
    return (
      currentFileType?.count > 0 ||
      (selectedFile === "Similarity Report" &&
        similarityReport.status !== "valid")
    );
  }, [fileArray, selectedFile, similarityReport.status]);

  const handleVersionToggle = useCallback((proposalType, version) => {
    const key = `${proposalType}-${version}`;
    setExpandedVersions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const getStatusText = (status) => {
    switch (status) {
      case "Approved":
        return "Approved";
      case "Pending":
        return "Under Review";
      case "Returned":
        return "Modify";
      case "Submitted":
        return "Submitted";
      case "active":
        return "Active";
      case "locked":
        return "Locked";
      default:
        return "";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "#049300";
      case "Pending":
        return "#f59e0b";
      case "Returned":
        return "#dc2626";
      case "Submitted":
        return "#6366f1";
      case "active":
        return "#3b82f6";
      case "locked":
        return "#9ca3af";
      default:
        return "#6b7280";
    }
  };

  return (
    <SubmissionSection>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {["Ongoing", "Returned", "Active"].includes(
            assignmentDetails?.status
          ) && <SubmissionText>SUBMIT ASSIGNMENT</SubmissionText>}

          {/* Merged Tab Section - File Types + Proposals */}
          {[
            "Ongoing",
            "Closed",
            "Returned",
            "Active",
            "Approved",
            "Submitted",
          ].includes(assignmentDetails?.status) && (
            <div>
              <FileTypeSelection>
                <InnerFileTypeSelection>
                  {/* Dynamic File Type Tabs from file_requirements */}
                  {fileArray?.map((item, index) => {
                    const fileTypeStatus = fileTypeStatuses[index];
                    const isLocked = fileTypeStatus === "locked";
                    const isSelected = selectedFile === item.file_type;

                    return (
                      <TabContainer
                        key={item.file_type}
                        selected={isSelected}
                        locked={isLocked}
                      >
                        <FileType
                          style={{
                            backgroundColor: isSelected
                              ? getFileTypeColor(selectedFile, item.file_type)
                              : isLocked
                              ? "#f3f4f6"
                              : "#ffffff",
                            cursor: isLocked ? "not-allowed" : "pointer",
                            opacity: isLocked ? 0.6 : 1,
                            pointerEvents: isLocked ? "none" : "auto",
                            minWidth:
                              selectedFile === item.file_type ? "160px" : "80px",
                          }}
                          onClick={() => handleFileSelection(item.file_type)}
                        >
                          {item.file_type}
                        </FileType>
                      </TabContainer>
                    );
                  })}
                </InnerFileTypeSelection>
              </FileTypeSelection>

              <AssignmentSubmissionContainer>
                {/* Show file upload area for selected file type */}
                {selectedFile ? (
                  <>
                    <SubmittingContainer
                      style={{
                        cursor: isDropZoneActive ? "pointer" : "default",
                        backgroundColor: isDragging ? "#f0f8ff" : "transparent",
                        borderColor: isDragging ? "#4048AE" : "#9d9d9d",
                      }}
                      onDragEnter={
                        isDropZoneActive ? handleDragEnter : undefined
                      }
                      onDragLeave={
                        isDropZoneActive ? handleDragLeave : undefined
                      }
                      onDragOver={isDropZoneActive ? handleDragOver : undefined}
                      onDrop={isDropZoneActive ? handleDrop : undefined}
                    >
                      {isDragging && isDropZoneActive ? (
                        <DragActiveContent>
                          <FileIcon
                            style={{
                              width: "80px",
                              height: "80px",
                              opacity: 0.8,
                            }}
                            src={fileUploadIcon}
                            alt="file_upload_icon"
                          />
                          <DragText>Drop your files here</DragText>
                        </DragActiveContent>
                      ) : (
                        <>
                          <FileIcon
                            style={{ width: "50px", height: "65px" }}
                            src={fileUploadIcon}
                            alt="file_upload_icon"
                          />
                          <UploadMainText>
                            <span style={{ color: "#4048AE" }}>
                              Drag and Drop
                            </span>{" "}
                            your file here
                          </UploadMainText>

                          {(() => {
                            const currentFileIndex = fileArray?.findIndex(
                              (item) => item.file_type === selectedFile
                            );
                            const currentStatus =
                              currentFileIndex !== -1
                                ? fileTypeStatuses[currentFileIndex]
                                : null;

                            const isCompleted =
                              currentStatus === "Approved" ||
                              currentStatus === "Submitted";

                            if (
                              !isCompleted &&
                              fileArray?.find(
                                (item) => item.file_type === selectedFile
                              )?.count > 0
                            ) {
                              return (
                                <FileCountDiv>
                                  {selectedFile === "Similarity Report" &&
                                  similarityReport.file === null ? (
                                    <>
                                      Drop your{" "}
                                      <span style={{ color: "#DF0000" }}>
                                        PDF
                                      </span>{" "}
                                      file here
                                    </>
                                  ) : selectedFile === "Similarity Report" &&
                                    similarityReport.file !== null &&
                                    similarityReport.status === "invalid" ? (
                                    <InvalidReportContainer>
                                      <ReuploadSection>
                                        <div style={{ color: "#007BFF" }}>
                                          reupload
                                        </div>
                                        <FileIcon
                                          src={reUploadIcon}
                                          alt="re-upload icon"
                                          style={{
                                            height: "15px",
                                            width: "15px",
                                            marginLeft: "3px",
                                          }}
                                        />
                                      </ReuploadSection>
                                      <ErrorMessage>
                                        The similarity index exceeds 10%. Please
                                        re-upload the assignment files.
                                      </ErrorMessage>
                                    </InvalidReportContainer>
                                  ) : selectedFile === "Similarity Report" &&
                                    similarityReport.file !== null &&
                                    similarityReport.status === "valid" ? (
                                    <CompletedSection>
                                      <CompletedText>completed</CompletedText>
                                      <FileIcon
                                        src={tickIconGreen}
                                        alt="tick"
                                        style={{
                                          height: "15px",
                                          width: "15px",
                                          marginLeft: "3px",
                                        }}
                                      />
                                    </CompletedSection>
                                  ) : (
                                    <>
                                      Drop{" "}
                                      <span
                                        style={{
                                          color: getExtensionTextColor(
                                            fileArray?.find(
                                              (item) =>
                                                item.file_type === selectedFile
                                            )?.extension || selectedFile
                                          ),
                                        }}
                                      >
                                        {fileArray
                                          ?.find(
                                            (item) =>
                                              item.file_type === selectedFile
                                          )
                                          ?.extension?.toUpperCase() ||
                                          selectedFile}
                                      </span>{" "}
                                      file here X
                                      {
                                        fileArray?.find(
                                          (item) =>
                                            item.file_type === selectedFile
                                        )?.count
                                      }
                                    </>
                                  )}
                                </FileCountDiv>
                              );
                            } else {
                              return (
                                <CompletedSection>
                                  <CompletedText>completed</CompletedText>
                                  <FileIcon
                                    src={tickIconGreen}
                                    alt="tick"
                                    style={{
                                      height: "15px",
                                      width: "15px",
                                      marginLeft: "3px",
                                    }}
                                  />
                                </CompletedSection>
                              );
                            }
                          })()}

                          {(fileArray?.find(
                            (item) =>
                              item.file_type === selectedFile &&
                              selectedFile !== "Similarity Report"
                          )?.count > 0 ||
                            (selectedFile === "Similarity Report" &&
                              similarityReport.file !== null &&
                              similarityReport.status === "invalid")) &&
                            !(
                              fileArray?.findIndex(
                                (item) => item.file_type === selectedFile
                              ) !== -1 &&
                              ["Approved", "Submitted"].includes(
                                fileTypeStatuses[
                                  fileArray.findIndex(
                                    (item) => item.file_type === selectedFile
                                  )
                                ]
                              )
                            ) && (
                              <UploadButton>
                                <UploadButtonText>Choose file</UploadButtonText>
                                <input
                                  ref={fileInputRef}
                                  name="myImage"
                                  type="file"
                                  id="file1"
                                  accept={getAcceptedFileTypes(
                                    selectedFile,
                                    fileArray
                                  )}
                                  style={{ display: "none" }}
                                  multiple={
                                    selectedFile !== "Similarity Report"
                                  }
                                  onChange={
                                    selectedFile !== "Similarity Report"
                                      ? handleFileUpload
                                      : handleReport
                                  }
                                />
                              </UploadButton>
                            )}
                        </>
                      )}
                    </SubmittingContainer>

                    <LoadedFilesDiv>
                      <FilesGrid>
                        {/* Dynamic dissertation files rendering */}
                        {dissertationFiles[selectedFile] &&
                          dissertationFiles[selectedFile].length > 0 &&
                          dissertationFiles[selectedFile].map((item, index) => (
                            <LoadedFileDiv
                              key={`${selectedFile}-${index}`}
                              file={item}
                              setUploadedFiles={() => {}}
                              setFileArray={setFileArray}
                              setSubmitButton={setSubmitButton}
                              normalizeFileType={normalizeFileType}
                              selectedFile={selectedFile}
                              onDelete={() =>
                                handleDeleteDissertationFile(
                                  selectedFile,
                                  index
                                )
                              }
                            />
                          ))}

                        {/* Show regular uploaded files for other file types (if not dissertation type) */}
                        {!fileArray.some(
                          (item) => item.file_type === selectedFile
                        ) &&
                          uploadedFiles.map((file, index) => (
                            <LoadedFileDiv
                              key={index}
                              file={file}
                              setUploadedFiles={setUploadedFiles}
                              setFileArray={setFileArray}
                              setSubmitButton={setSubmitButton}
                              normalizeFileType={normalizeFileType}
                              selectedFile={selectedFile}
                            />
                          ))}
                      </FilesGrid>
                      <SubmitButtonContainer>
                        <SubmitButton
                          style={{
                            backgroundColor: submitButton
                              ? "#917AFD"
                              : "#828282ff",
                          }}
                          disabled={!submitButton}
                          onClick={() => submitInitialFiles()}
                        >
                          Submit
                        </SubmitButton>
                      </SubmitButtonContainer>
                    </LoadedFilesDiv>
                  </>
                ) : null}
              </AssignmentSubmissionContainer>

              {/* Submission History for Selected File Type */}
              {selectedFile && fileArray?.length > 0 && (
                <>
                  {(() => {
                    const fileTypeIndex = fileArray.findIndex(
                      (item) => item.file_type === selectedFile
                    );
                    const submissions =
                      processedSubmissions[fileTypeIndex] || [];

                    if (submissions.length === 0) return null;

                    return (
                      <SubmissionsSection>
                        <SubmissionsViewContainer>
                          {submissions
                            ?.sort((a, b) => b.version - a.version)
                            .map((submission) => (
                              <VersionAccordion key={submission.version}>
                                <VersionHeader
                                  onClick={() =>
                                    handleVersionToggle(
                                      fileTypeIndex,
                                      submission.version
                                    )
                                  }
                                >
                                  <VersionInfo>
                                    <VersionTitle>
                                      {getSectionNameByChapter(
                                        submission.chapter,
                                        fileArray
                                      )}
                                    </VersionTitle>
                                    <VersionMeta>
                                      <span>
                                        {timeFormatter(
                                          submission.submittedDate
                                        )}
                                      </span>
                                      <StatusIndicator
                                      status={submission.status}
                                        style={{
                                          color: getStatusColor(
                                            submission.status
                                          ),
                                        }}
                                      >
                                        {getStatusText(submission.status)}
                                      </StatusIndicator>
                                    </VersionMeta>
                                  </VersionInfo>
                                  <ChevronIcon
                                    src={
                                      expandedVersions[
                                        `${fileTypeIndex}-${submission.version}`
                                      ]
                                        ? chevronUp
                                        : chevronDown
                                    }
                                    alt="toggle"
                                  />
                                </VersionHeader>

                                {expandedVersions[
                                  `${fileTypeIndex}-${submission.version}`
                                ] && (
                                  <VersionContent>
                                    <SubmissionDetails>
                                      {submission.feedback && (
                                        <FeedbackSection>
                                          <FeedbackLabel>
                                            Feedback
                                            {submission.feedbackDate && (
                                              <span
                                                style={{
                                                  fontWeight: "400",
                                                  fontSize: "12px",
                                                  color: "#656565",
                                                  marginLeft: "10px",
                                                }}
                                              >
                                                {timeFormatter(
                                                  submission.feedbackDate
                                                )}
                                              </span>
                                            )}
                                          </FeedbackLabel>
                                          <FeedbackDisplay>
                                            {submission.feedback}
                                          </FeedbackDisplay>
                                        </FeedbackSection>
                                      )}

                                      {submission.upload && (
                                        <FacultyFeedbackSection>
                                          <FileSectionTitle>
                                            Faculty Feedback File
                                          </FileSectionTitle>
                                          <SubmittedFilesGrid>
                                            <SubmittedFileCard
                                              href={submission.upload}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              <FileIconWrapper>
                                                <img
                                                  src={getFileIcon(
                                                    submission.feedback_file_type
                                                  )}
                                                  alt="file"
                                                />
                                              </FileIconWrapper>
                                              <SubmittedFileDetails>
                                                <SubmittedFileName>
                                                  {
                                                    submission.feedback_file_name
                                                  }
                                                </SubmittedFileName>
                                                <SubmittedFileType>
                                                  {
                                                    submission.feedback_file_type
                                                  }
                                                </SubmittedFileType>
                                              </SubmittedFileDetails>
                                            </SubmittedFileCard>
                                          </SubmittedFilesGrid>
                                        </FacultyFeedbackSection>
                                      )}

                                      <FilesSection>
                                        <div>
                                          <FileSectionTitle>
                                            Student File
                                          </FileSectionTitle>
                                          <SubmittedFilesGrid>
                                            {submission.files.map(
                                              (file, fileIndex) => (
                                                <SubmittedFileCard
                                                  key={fileIndex}
                                                  href={file.fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  <FileIconWrapper>
                                                    <img
                                                      src={getFileIcon(
                                                        file.fileType
                                                      )}
                                                      alt="file"
                                                    />
                                                  </FileIconWrapper>
                                                  <SubmittedFileDetails>
                                                    <SubmittedFileName>
                                                      {file.fileName}
                                                    </SubmittedFileName>
                                                    <SubmittedFileType>
                                                      {file.fileType}
                                                    </SubmittedFileType>
                                                  </SubmittedFileDetails>
                                                </SubmittedFileCard>
                                              )
                                            )}
                                          </SubmittedFilesGrid>
                                        </div>
                                      </FilesSection>
                                    </SubmissionDetails>
                                  </VersionContent>
                                )}
                              </VersionAccordion>
                            ))}
                        </SubmissionsViewContainer>
                      </SubmissionsSection>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </>
      )}
    </SubmissionSection>
  );
};

export default SubmitDissertation;

// ============= STYLED COMPONENTS =============

const SubmissionSection = styled.div`
  margin-top: 30px;
`;

const SubmissionText = styled.p`
  text-transform: uppercase;
  font-size: 16px;
  color: #9d9d9d;
  margin-top: 30px;
  margin-bottom: 20px;
`;

const FileTypeSelection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const InnerFileTypeSelection = styled.div`
  min-width: 70%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
`;

const TabContainer = styled.div`
  border: ${(props) => (props.selected ? "1px solid #9d9d9d" : "none")};
  border-bottom: ${(props) => (props.selected ? "2px solid white" : "none")};
  border-radius: ${(props) => (props.selected ? "14px 14px 0 0" : "0")};
  padding: ${(props) => (props.selected ? "5px 5px 0 5px" : "0")};
  opacity: ${(props) => (props.locked ? "0.5" : "1")};
`;

const FileType = styled.div`
  height: 45px;
  padding: 10px;
  text-align: center;
  color: #313131;
  border-radius: 9px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const AssignmentSubmissionContainer = styled.div`
  border: 1px solid #9d9d9d;
  margin-top: -1px;
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  padding: 20px 30px;
`;

const SubmittingContainer = styled.label`
  border: 2px dashed #9d9d9d;
  box-sizing: border-box;
  border-radius: 10px;
  width: 100%;
  height: 350px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  margin-bottom: 20px;
  transition: all 0.3s ease;
`;

const DragActiveContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  transition: all 0.3s ease;
`;

const DragText = styled.div`
  font-size: 24px;
  color: #4048ae;
  font-weight: bold;
  margin-top: 20px;
`;

const FileIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const UploadMainText = styled.div`
  font-size: 22.25px;
  line-height: 160%;
`;

const FileCountDiv = styled.div`
  color: #797979;
  text-align: center;
  font-size: 18px;
  line-height: 160%;
`;

const CompletedSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #09c425;
  transition: all 0.3s ease;
`;

const CompletedText = styled.div`
  color: #09c425;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%;
  letter-spacing: 0.72px;
`;

const InvalidReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ReuploadSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 13px;
  text-align: center;
  max-width: 300px;
`;

const UploadButton = styled.label`
  padding: 12px 10px;
  border-radius: 9px;
  width: 120px;
  background: #e9e9e9;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #d4d4d4;
  }
`;

const UploadButtonText = styled.p`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: -2px;
  color: #000000;
`;

const LoadedFilesDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;
`;

const FilesGrid = styled.div`
  width: 100%;
  gap: 10px;
  display: flex;
  flex-wrap: wrap;
`;

const FileCard = styled.div`
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.25);
  min-width: 220px;
  height: 70px;
  border-radius: 8px;
  display: flex;
  padding: 10px;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.3);
  }
`;

const FileCardLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  flex-direction: column;
  flex: 1;
`;

const FileCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex: 1;
  gap: 10px;
`;

const FileInfo = styled.div``;

const FileName = styled.div`
  font-size: 15px;
  font-weight: bold;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled.div`
  display: flex;
`;

const FileSize = styled.div`
  font-size: 12px;
  margin-right: 10px;
`;

const FileDate = styled.div`
  font-size: 12px;
`;

const DeleteButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubmitButtonContainer = styled.div`
  margin-right: 10px;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  align-items: start;
`;

const SubmitButton = styled.button`
  display: inline-block;
  height: 40px;
  padding: 12px 20px;
  background: #917afd;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.05rem;
  color: #fff;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #8069e8;
    box-shadow: 0 4px 8px rgba(145, 122, 253, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #828282ff;
  }
`;

const SubmissionsSection = styled.div`
  margin-top: 30px;
  width: 100%;
`;

const SubmissionsViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VersionAccordion = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const VersionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const VersionInfo = styled.div`
  flex: 1;
`;

const VersionTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 160%;
  color: #1f2937;
  margin-bottom: 4px;
`;

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 400;
  font-size: 12px;
  line-height: 160%;
  color: #656565;
`;

const StatusIndicator = styled.div`
  font-weight: 500;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "approved":
        return "#dcfce7";
      case "returned":
        return "#fee2e2";
      case "pending":
        return "#fef3c7";
      case "submitted":
        return "#dbeafe";
      default:
        return "#f3f4f6";
    }
  }};
`;

const ChevronIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const VersionContent = styled.div`
  padding: 0 24px 16px 24px;
`;

const SubmissionDetails = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
`;

const FeedbackSection = styled.div`
  margin-bottom: 1rem;
`;

const FeedbackLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 8px;
`;

const FeedbackDisplay = styled.div`
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  line-height: 1.5;
`;

const FacultyFeedbackSection = styled.div`
  margin-top: 16px;
  margin-bottom: 1rem;
`;

const FileSectionTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const SubmittedFilesGrid = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 12px;
`;

const SubmittedFileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: linear-gradient(to right, #f0f4ff, #e8f0ff);
    border-color: #c7d2fe;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  }
`;

const FileIconWrapper = styled.div`
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

const SubmittedFileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const SubmittedFileName = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 2px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubmittedFileType = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilesSection = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;
